import { createTheme } from '@mui/material/styles'

const defaultTheme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1200,
      xl: 1366,
    },
  },
  palette: {
    primary: {
      main: '#ff7247',
      light: '#eabbac',
      extraLight: '#faf1ee',
      text: '#868686',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#0B3D62 !important',
    },
    tertiary: {
      main: '#FDB814 !important',
    },
    background: {
      default: '#F4F7FE',
      light: '#F3F3F3 !important',
      medium: '#EDEDED',
      paper: '#FFFFFF',
    },
    missing: {
      main: '#AAAAAA',
    },
    border: {
      default: '#E7E7E7',
    },
    cardColor: {
      primary: {
        color: '#FFFFFF',
        background: 'linear-gradient(270deg, #01288D 0%, #0183CE 100%)',
      },
      secondary: {
        color: '#FFFFFF',
        background: 'linear-gradient(270deg, #AF7B3D 0%, #F28C35 100%)',
      },
      tertiary: {
        color: '#FFFFFF',
        background: 'linear-gradient(270deg, #8E2B0E 0%, #D65A77 100%)',
      },
    },
    alert: {
      warning: '#FDB814',
      error: '#ff5560',
    },
  },
  chartsPalette: [
    '#0E2244',
    '#D6775A',
    '#7368AB',
    '#ff7247',
    '#1F0D78',
    '#B34220',
    '#2C68D1',
    '#FFA185',
    '#1c6c6c',
    '#a8dabf',
  ],
  typography: {
    fontFamily: [
      'Roboto',
      'Montserrat',
      'Helvetica',
      'Arial',
      'sans-serif',
    ].join(','),
    body1: {
      fontSize: '0.75rem', // 12px
    },
    body2: {
      fontSize: '0.6875rem !important', // 11px
    },
    button: {
      fontSize: '0.75rem', // 12px
    },
    caption: {
      fontSize: '0.625rem !important', // 10px
    },
    h1: {
      fontSize: '3rem', // 48px
    },
    h2: {
      fontSize: '2.25rem', // 36px
    },
    h3: {
      fontSize: '1.875rem !important', // 30px
      marginBottom: '23px',
    },
    h4: {
      fontSize: '1.5rem', // 24px
    },
    h5: {
      fontSize: '0.75rem !important', // 12px
      marginBottom: '23px',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    h6: {
      fontSize: '1rem', // 16px
    },
    subtitle1: {
      fontSize: '0.875rem !important', // 14px
    },
    subtitle2: {
      fontSize: '0.8125rem', // 13px
    },
    jumbo1: {
      fontSize: '5.5rem', // 88px
    },
    jumbo2: {
      fontSize: '4.5rem', // 72px
    },
  },
  mediaQueries: {
    ie11: '@media screen and (-ms-high-contrast: active), screen and (-ms-high-contrast: none)',
  },
  layout: {
    header: {
      height: 70,
      responsiveMenu: {
        minWidth: 300,
      },
    },
    login: {
      mainBlocWidth: 800,
    },
    borderRadius: 6,
    CardOverview: {
      width: '250px',
      value: {
        background: '#D6775A',
        color: 'white',
        fontSize: '1.875rem !important',
      },
    },
    CardFilters: {
      width: '314px',
      title: {
        textTransform: 'none',
      },
      action: {
        color: '#FFFFFF !important',
      },
    },
    avatar: {
      width: 46, // px
      height: 46, // px
    },
    logo: {
      height: '28px',
    },
  },
  transition: {
    animation: (target = 'all') =>
      `${target} 0.3s cubic-bezier(.25,.8,.25,1) !important`,
  },
  shadow: {
    default: '0 0 13px 0 rgba(82,63,105,.1)',
    hover: '2px 2px 13px 0 rgba(82,63,105,.3)',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: ['Montserrat', 'Helvetica', 'Arial', 'sans-serif'],
          fontSize: '0.8125rem',
          textTransform: 'none',
          borderRadius: 6,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          minHeight: 38,
          borderRadius: 6,
        },
        input: {
          paddingTop: 3,
          paddingBottom: 3,
          paddingLeft: 3,
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          marginBottom: '20px',
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          lineHeight: '20px',
          margin: '0 0 10px',
          textTransform: 'uppercase',
          position: 'static',
          fontWeight: 'bold',
          color: '#282D30',
          letterSpacing: '0.5px',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '12px',
          position: 'static',
          transform: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '20px',
          boxShadow: '0 2px 23px #E2E5F1',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '25px 22px 22px',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          boxShadow: '0px 2px 23px #EDEDED',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          textDecoration: 'none',
        },
      },
    },
    DiagnosticResult: {
      DPE: {
        A: {
          main: '#00963e',
          background: 'transparent',
          contrastText: 'white',
        },
        B: {
          main: '#74b82a',
          background: 'transparent',
          contrastText: 'white',
        },
        C: {
          main: '#c7d500',
          background: 'transparent',
          contrastText: 'white',
        },
        D: {
          main: '#feed00',
          background: 'transparent',
          contrastText: 'white',
        },
        E: {
          main: '#fbbb00',
          background: 'transparent',
          contrastText: 'white',
        },
        F: {
          main: '#ef7d00',
          background: 'transparent',
          contrastText: 'white',
        },
        G: {
          main: '#e40613',
          background: 'transparent',
          contrastText: 'white',
        },
      },
      GES: {
        A: {
          main: '#FCF0F4',
          background: 'transparent',
          contrastText: 'white',
        },
        B: {
          main: '#DAC1DC',
          background: 'transparent',
          contrastText: 'white',
        },
        C: {
          main: '#C7A9CB',
          background: 'transparent',
          contrastText: 'white',
        },
        D: {
          main: '#B692C0',
          background: 'transparent',
          contrastText: 'white',
        },
        E: {
          main: '#A075AD',
          background: 'transparent',
          contrastText: 'white',
        },
        F: {
          main: '#82599B',
          background: 'transparent',
          contrastText: 'white',
        },
        G: {
          main: '#69408E',
          background: 'transparent',
          contrastText: 'white',
        },
      },
    },
  },
})

export default defaultTheme
